"use client";
import { useEffect, useState, useRef, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useIsMobile } from "@/utils/useIsMobile";
import Icon from "@/components/icons/Icons";

interface Section {
  id: string;
  heading: string;
  content: string;
}

interface SeoSectionProps {
  htmlContent?: string;
  dark?: boolean;
}

export default function CommonSeoSection({ htmlContent, dark = false }: SeoSectionProps) {
  const [sections, setSections] = useState<Section[]>([]);
  const [activeIndex, setActiveIndex] = useState<number | null>(0);
  const isMob = useIsMobile();
  const containerRef = useRef<HTMLDivElement>(null);
  const sectionRefs = useRef<Array<HTMLDivElement | null>>([]);
  useEffect(() => {
    if (!htmlContent) return;

    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, "text/html");
    const body = doc.body;
    const tags = Array.from(body.children);
    const extracted: Section[] = [];
    let currentHeading = "";
    let currentContent = "";

    tags.forEach((tag) => {
      if (tag.tagName.toLowerCase() === "h2") {
        if (currentHeading) {
          extracted.push({
            id: `section${extracted.length + 1}`,
            heading: currentHeading,
            content: currentContent,
          });
        }

        currentHeading = tag.textContent?.trim() || "";
        currentContent = "";
      } else {
        currentContent += tag.outerHTML;
      }
    });

    if (currentHeading) {
      extracted.push({
        id: `section${extracted.length + 1}`,
        heading: currentHeading,
        content: currentContent,
      });
    }

    setSections(extracted);
  }, [htmlContent]);

  const handleAccordion = useCallback(
    (index: number) => {
      if (!isMob) return;
      setActiveIndex(activeIndex === index ? null : index);
    },
    [activeIndex, isMob]
  );

  useEffect(() => {
    if (isMob) return;

    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const headerOffset = 50;
      let active = 0;

      sectionRefs.current.forEach((el, idx) => {
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();

        // Element top relative to container
        const relativeTop = rect.top - containerRect.top;

        if (relativeTop <= headerOffset) {
          active = idx;
        }
      });

      setActiveIndex(active);
    };

    container.addEventListener("scroll", handleScroll);
    handleScroll();

    return () => container.removeEventListener("scroll", handleScroll);
  }, [isMob, sections]);

  const handleScrollButtons = (idx: number) => {
    if (!isMob) scrollToSection(idx);
    else handleAccordion(idx);
  };

  const scrollToSection = (index: number) => {
    const el = sectionRefs.current[index];
    const container = containerRef.current;
    if (!el || !container) return;

    const headerOffset = 20; // space from top inside container
    const top = el.offsetTop - headerOffset;

    container.scrollTo({
      top,
      behavior: "smooth",
    });
  };

  if (!htmlContent) return;

  return (
    <div className={`seo-content-common border sm:border-2 ${dark ? "border-[#d4d4d4] seo-content-dark" : "border-[#4d4d4d] bg-white seo-content-light"} relative p-4 sm:p-8 rounded-2xl sm:rounded-8xl sm:max-h-158.75 sm:overflow-y-auto scrollbar-none`} ref={containerRef}>
      <div className="new-content-box">
        {/* ===== Left Table of Contents ===== */}
        {!isMob && (
          <div className="hidden 2md:block w-1/3 space-y-10 sticky top-3 z-1">
            {sections.map((sec, idx) => (
              <button key={idx} className={`lg:text-xl lg:leading-7 leading-1.5 w-full text-left ${activeIndex === idx ? "text-[#5394ff] font-medium" : dark ? "text-white" :"text-primaryBlack"}`} onClick={() => handleScrollButtons(idx)}>
                {sec.heading}
              </button>
            ))}
          </div>
        )}

        {/* ===== Right Side Sections ===== */}
        <div className="w-full 2md:w-3/5">
          {sections.map((sec, idx) => {
            const isActive = isMob && activeIndex === idx;
            return (
              <div
                key={idx}
                className={`detail-section border-b last-of-type:border-0 sm:border-0 ${dark ? "border-white/20" :"border-black/20"}`}
                ref={(el) => {
                  sectionRefs.current[idx] = el;
                }}
              >
                <h2 className={`headings flex items-center justify-between collapse-head ${isActive ? "text-primaryBlue" : dark ? "text-white" :"text-primaryBlack"}`} onClick={() => handleAccordion(idx)}>
                  {sec.heading}

                  <Icon.dropdownDownArrow className={`sm:hidden shrink-0 transition duration-300 ${isActive && "rotate-180"}`} />
                </h2>

                {isMob ? (
                  <AnimatePresence>
                    {isActive && (
                      <motion.div className="collapse-section" initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.35, ease: "easeInOut" }}>
                        <div dangerouslySetInnerHTML={{ __html: sec.content }} />
                      </motion.div>
                    )}
                  </AnimatePresence>
                ) : (
                  <div className="collapse-section" dangerouslySetInnerHTML={{ __html: sec.content }} />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
